import { supabase } from '@/integrations/supabase/client';

const HOMEJAB_ID = '8bbd1638-3495-42f4-9ceb-ba9f1479b0d3';

const homeJabData = {
  core: {
    title: "HomeJab",
    subtitle: "On-Demand Real Estate Photography & Media Service",
    description: "HomeJab is an on-demand professional real estate photography service that connects agents with vetted local photographers to capture high-quality listing photos, videos, 3D tours, and more, with a 24-hour turnaround.",
    service_type: "service",
    status: "ready",
    is_published: false,
    is_hot: false,
    is_select: false
  },
  classification: {
    categories: ["Real Estate Photography", "Listing Media Services"],
    tags: ["photography", "videography", "drone", "virtual tours", "virtual staging", "floor plans", "real estate marketing", "on-demand service"],
    best_for: ["Real estate listing agents", "Brokerages needing professional listing media", "Agents seeking fast turnaround on photos", "Agents marketing luxury or visually appealing properties"]
  },
  pricing: {
    price: 175,
    msrp: 210.0,
    price_duration: "package"
  },
  media: {
    image: "",
    profile_image: "",
    gallery: ["https://www.youtube.com/watch?v=UoLzNdH1_Gg", "https://www.youtube.com/watch?v=cZ5xzwX3O-Q"],
    demo_video_url: "https://www.youtube.com/watch?v=UoLzNdH1_Gg",
    podcast_audio_url: null,
    podcast_title: null
  },
  feature_highlights: [
    { icon: "Users", text: "Network of vetted local photographers in every major market", type: "lucide" },
    { icon: "RefreshCw", text: "Fast turnaround – media delivered within 24 hours after the shoot", type: "lucide" },
    { icon: "TrendingUp", text: "High-quality visuals that help listings sell faster and for more", type: "lucide" }
  ],
  roi_metrics: {
    time_saved: "Hours saved per listing by outsourcing photography and editing",
    cost_saved: "No need to invest in camera gear or software; pay per project",
    revenue_increase: "Homes with professional photos can sell for higher prices and faster",
    payback_period: null,
    average_roi: null,
    time_to_results: "Next-day listing enhancement with pro photos",
    key_outcomes: [
      "More buyer interest in listings",
      "Shorter time on market",
      "Enhanced professional image for agent"
    ]
  },
  setup_info: {
    setup_time: "Minutes to book a shoot online",
    difficulty: "Easy",
    support_included: true,
    timeline: [
      { step: "Sign up and schedule shoot via HomeJab platform", time_estimate: "5-10 minutes" },
      { step: "Photographer arrives and shoots property", time_estimate: "1-2 hours" },
      { step: "Photos & media edited and delivered", time_estimate: "24 hours after shoot" }
    ]
  },
  at_a_glance: {
    quick_summary: "Professional real estate photography service that lets agents order listing photos, videos, and 3D tours on-demand, with a nationwide photographer network and 24-hour turnaround.",
    who_its_for: {
      typical_profile: "Real estate agents and brokerages in need of high-quality listing media",
      skill_level: "Any (no technical photography skills required)",
      other_criteria: [
        "Agents who want to outsource photography",
        "Those looking for fast, reliable turnaround",
        "Available across all US markets"
      ]
    },
    what_you_get: [
      "Professional listing photos (standard or HDR)",
      "HD video walkthroughs",
      "3D virtual tours and floor plans",
      "Aerial drone photos/videos",
      "Virtual staging services"
    ],
    speed_to_value: {
      setup_time: "Just 24-48 hours lead time to schedule",
      first_impact: "Within 1-2 days (photos delivered the day after the shoot, ready to enhance your listing)"
    },
    commitment: {
      minimum_period: "No long-term commitment – order per listing as needed",
      cancellation_terms: "Cancel or reschedule up to 2 hours before shoot without fee; $50 fee if within 2 hours"
    },
    guarantee: { type: null, terms: null }
  },
  features: [
    { title: "On-Demand Scheduling", description: "Easily book a professional shoot online 24/7 without phone tag or negotiations – HomeJab's platform handles finding a photographer and scheduling within 1-2 days." },
    { title: "High-Quality Media", description: "Delivers high-end images and videos including HDR photography, 4K video tours, aerial drone shots, immersive 3D tours, and even virtual staging, all edited by professionals." },
    { title: "Nationwide Network", description: "Access to over 2,800 vetted real estate photographers across all 50 states, ensuring local expertise and availability in every major market." },
    { title: "Fast Turnaround", description: "Photos and other media are delivered within 24 hours after the shoot, so you can get your listing live quickly with top-notch visuals." },
    { title: "One-Stop Shop", description: "Comprehensive service covers all listing media needs – order photos, videos, 3D tours, floor plans, and more from one platform, with everything delivered ready for MLS and marketing use." }
  ],
  feature_sections: [
    { headline: "High-End Photography & Video", imageUrl: null, imageIndex: 0, bullets: ["Professional photographers capture well-composed, well-lit images that make properties shine.", "Advanced HDR techniques ensure vibrant colors and clear window views for a true-to-life look.", "Video walkthroughs highlight property flow and features in motion, engaging buyers with a tour-like experience."] },
    { headline: "Fast & Convenient Service", imageUrl: null, imageIndex: 1, bullets: ["Book shoots online in minutes for any day of the week; photographers available 7 days a week in most areas.", "Quick turnaround: media is edited and delivered by the next business day, keeping your marketing on schedule.", "HomeJab handles editing and processing, so agents can focus on clients while the visuals are handled by pros."] },
    { headline: "Sell Listings Faster", imageUrl: null, imageIndex: 2, bullets: ["Listings with professional photos get more views and attract more interest, which can lead to quicker sales.", "Using high-quality media elevates your brand as an agent and impresses sellers with top-notch marketing.", "HomeJab's track record: over 32,000 agents served and $55+ billion in real estate sold with HomeJab imagery backing the listings."] }
  ],
  service_highlights: [
    { icon: "Camera", title: "Professional Photos & Videos", description: "Expertly shot and edited listing photos, videos, and aerial footage to showcase properties at their best.", type: "lucide" },
    { icon: "Clock", title: "24-Hour Turnaround", description: "Lightning-fast delivery of edited media by the next day, keeping your listing launch on track.", type: "lucide" },
    { icon: "Globe2", title: "Nationwide Coverage", description: "Service available in every major U.S. market with a network of photographers ready to go.", type: "lucide" },
    { icon: "Shield", title: "No Hassle Ordering", description: "One-stop platform handles scheduling, editing, and delivery—no need to coordinate multiple vendors for photos, tours, and more.", type: "lucide" }
  ],
  company_info: {
    founder: "Joe Jesuele",
    founded_year: "2012",
    company_type: "Private",
    team_size: "25 (core team) plus 2,800+ photographers network",
    funding: null,
    website: "https://www.homejab.com",
    mission: "To be the easiest and most efficient one-stop-shop photography platform in the real estate industry.",
    headquarters: "Cherry Hill, New Jersey, USA",
    clients_served: "32,000+ real estate professionals (U.S. and Canada)",
    about: "HomeJab is a real estate photography marketplace that allows agents to order professional photos, videos, and 3D tours on-demand, without the hassle of negotiating or dealing with individual photographers. Its network of 2,800+ photographers has delivered over 6 million photos, helping sell more than $55 billion in properties."
  },
  contact: {
    vendor_email: "[email protected]",
    website_url: "https://www.homejab.com",
    booking_url: "https://YOUR-CIRCLE-BOOKING-URL-HERE"
  },
  metadata: {
    rating: 4.8,
    review_count: 1100
  },
  plans: [
    { name: "Standard Photo Shoot (15 photos)", price: 175, original_price: null, features: ["15 edited MLS-ready photos", "Local professional photographer", "24-hour turnaround time"], target_audience: "Typical home listing", sort_order: 0 },
    { name: "Standard Photo Shoot (30 photos)", price: 195, original_price: null, features: ["30 edited photos", "Suitable for larger homes", "24-hour turnaround time"], target_audience: "Larger home listing", sort_order: 1 },
    { name: "HDR Photo Shoot (15 photos)", price: 229, original_price: null, features: ["15 HDR processed photos", "Enhanced lighting and color", "Ideal for high-end listings"], target_audience: "Luxury listing", sort_order: 2 },
    { name: "HDR Photo Shoot (30 photos)", price: 259, original_price: null, features: ["30 HDR processed photos", "Better for large or luxury homes", "24-hour turnaround time"], target_audience: "Luxury or large listing", sort_order: 3 },
    { name: "3D Tour + Photos Package (Standard)", price: 339, original_price: null, features: ["3D virtual tour (up to 4000 sq ft)", "15 photos included", "24-48 hr scheduling, 24 hr delivery"], target_audience: "Tech-forward listing presentation", sort_order: 4 },
    { name: "3D Tour + Photos Package (HDR)", price: 349, original_price: null, features: ["HDR 3D tour capture", "15 HDR photos included", "Best for premium properties"], target_audience: "High-end property showcase", sort_order: 5 }
  ],
  faq: [
    { question: "What areas does HomeJab serve and when are photographers available?", answer: "HomeJab is available in every major U.S. market across all 50 states, and you can schedule shoots 7 days a week.", sort_order: 0 },
    { question: "How quickly will I receive the photos and videos?", answer: "After your shoot takes place, your media is edited and delivered in one business day (photos usually within 24 hours). Note: Shoots done on Saturday deliver on Monday (no weekend delivery for 3D tours).", sort_order: 1 },
    { question: "Do I need to be at the property during the shoot?", answer: "No, you do not have to be present. You can provide access instructions (e.g., lockbox code) on the order form so the photographer can enter on their own.", sort_order: 2 },
    { question: "What is the cancellation policy for scheduled shoots?", answer: "You may reschedule or cancel by emailing support@circlenetwork.io. Cancellations are free up to 2 hours before the appointment; within 2 hours a $50 fee applies.", sort_order: 3 },
    { question: "How do I get my photos and media once they're ready?", answer: "You'll receive an email with links to download your photos and videos (MLS-ready and high-res versions). You can also log into your HomeJab account to access all your media anytime.", sort_order: 4 }
  ],
  reviews: [
    { author: "Nicole Busbin", title: null, content: "I had two experiences with HomeJab and was very pleased with both. The photographer was super professional and worked with me, allowing me to give input and select the pictures I liked most. They were on time and it was a quick experience. Will definitely use them again!", rating: 5, date: null, verified: true },
    { author: "Garry Grissom", title: null, content: "We used HomeJab when selling our home. The service is convenient and easy to use. Scheduling, rescheduling, questions, concerns, etc. are all handled promptly and professionally.", rating: 5, date: null, verified: true }
  ]
};

export async function updateHomeJabProduct(): Promise<void> {
  // 1. Update main product record
  const productUpdate = {
    ...homeJabData.core,
    categories: homeJabData.classification.categories,
    tags: homeJabData.classification.tags,
    best_for: homeJabData.classification.best_for,
    price: homeJabData.pricing.price,
    msrp: homeJabData.pricing.msrp,
    price_duration: homeJabData.pricing.price_duration,
    image: homeJabData.media.image,
    profile_image: homeJabData.media.profile_image,
    gallery: homeJabData.media.gallery,
    demo_video_url: homeJabData.media.demo_video_url,
    podcast_audio_url: homeJabData.media.podcast_audio_url,
    podcast_title: homeJabData.media.podcast_title,
    feature_highlights: homeJabData.feature_highlights,
    roi_metrics: homeJabData.roi_metrics,
    setup_info: homeJabData.setup_info,
    at_a_glance: homeJabData.at_a_glance,
    features: homeJabData.features,
    feature_sections: homeJabData.feature_sections,
    service_highlights: homeJabData.service_highlights,
    company_info: homeJabData.company_info,
    vendor_email: homeJabData.contact.vendor_email,
    website_url: homeJabData.contact.website_url,
    booking_url: homeJabData.contact.booking_url,
    rating: homeJabData.metadata.rating,
    review_count: homeJabData.metadata.review_count,
    updated_at: new Date().toISOString()
  };

  const { error: productError } = await supabase
    .from('products')
    .update(productUpdate)
    .eq('id', HOMEJAB_ID);

  if (productError) throw productError;

  // 2. Delete existing plans and insert new ones
  await supabase
    .from('product_plans')
    .delete()
    .eq('product_id', HOMEJAB_ID);

  const plansData = homeJabData.plans.map(plan => ({
    product_id: HOMEJAB_ID,
    name: plan.name,
    price: plan.price,
    original_price: plan.original_price,
    features: plan.features,
    target_audience: plan.target_audience,
    sort_order: plan.sort_order
  }));

  const { error: plansError } = await supabase
    .from('product_plans')
    .insert(plansData);

  if (plansError) throw plansError;

  // 3. Delete existing Q&A and insert new ones
  await supabase
    .from('product_qa')
    .delete()
    .eq('product_id', HOMEJAB_ID);

  const qaData = homeJabData.faq.map(qa => ({
    product_id: HOMEJAB_ID,
    question: qa.question,
    answer: qa.answer,
    sort_order: qa.sort_order
  }));

  const { error: qaError } = await supabase
    .from('product_qa')
    .insert(qaData);

  if (qaError) throw qaError;

  // 4. Delete existing reviews and insert new ones
  await supabase
    .from('product_reviews')
    .delete()
    .eq('product_id', HOMEJAB_ID);

  const reviewsData = homeJabData.reviews.map(review => ({
    product_id: HOMEJAB_ID,
    author: review.author,
    title: review.title,
    content: review.content,
    rating: review.rating,
    date: review.date || new Date().toISOString(),
    verified: review.verified
  }));

  const { error: reviewsError } = await supabase
    .from('product_reviews')
    .insert(reviewsData);

  if (reviewsError) throw reviewsError;
}
